/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderUserContext,
  FormStateBuilder,
  MONTH_NAME_LIST_MLS,
  REPORT_SALES_MANAGEMENT,
  ReportService,
} from '@gh/builder-shared';
import { reportField, SalesManagementCalculationType } from '@gh/builder-shared-data';
import { EnumDictionary, getDialogInitData } from '@gh/common-shared';
import { DateFormat, getDateMapper, RouteDataService } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { DialogInput, NotificationService, WaitingService } from '@gh/core-ui';

import { filter, find, get, head, isNil, map, values } from 'lodash';

import { LeadListBusinessService } from '../../../resources/lead-list-business.service';
import {
  composeMonthCode,
  decomposeMonthCode,
  SalesManagementReportFilter,
  SalesManagementReportFilterMonthSelectOption,
  SalesManagementReportHistoryItem,
} from '../../../resources/lead.model';
import { SalesManagementReportFilterFormState } from './sales-management-report-filter-form.service';
import { MatSelectChange } from '@angular/material/select';

export interface SalesManagementReportDialogConfig {
  forYear: number;
  forMonth: number;
}

@Component({
  standalone: false,
  selector: 'gh-sales-management-report-filter-dialog',
  templateUrl: 'sales-management-report-filter-dialog.component.html',
  styleUrls: ['sales-management-report-filter-dialog.component.scss'],
  providers: [
    SalesManagementReportFilterFormState,
  ],
})
export class SalesManagementReportFilterDialogComponent implements OnInit, DialogInput<SalesManagementReportDialogConfig> {
  input: SalesManagementReportDialogConfig;
  reportFilter: SalesManagementReportFilter;
  salesManagementCalculationTypes: EnumDictionary<SalesManagementCalculationType>;
  monthSelectOptionList: SalesManagementReportFilterMonthSelectOption[];

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              public form: SalesManagementReportFilterFormState,
              private dialogRef: MatDialogRef<any>,
              private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private notificationService: NotificationService,
              private formStateBuilder: FormStateBuilder,
              private waitingService: WaitingService,
              private translationService: TranslationService,
              private reportService: ReportService,
              private businessService: LeadListBusinessService) {
  }

  get isCalculateNewFiguresVisible(): boolean {
    const { forMonth, forYear } = decomposeMonthCode(this.form.getMonthCodeValue());
    return !get(find(this.reportFilter.months, { forMonth, forYear }), 'isFromHistory', false);
  }

  get isRecalculateFiguresVisible(): boolean {
    const lastHistoryMonth = head(filter(this.reportFilter.months, { isFromHistory: true }));
    const { forMonth, forYear } = decomposeMonthCode(this.form.getMonthCodeValue());
    const currentDateHistory = find(this.reportFilter.months, { forMonth, forYear });
    const isFromHistory = get(currentDateHistory, 'isFromHistory', false);
    return !!lastHistoryMonth
      && isFromHistory
      && lastHistoryMonth.forYear === forYear
      && lastHistoryMonth.forMonth === forMonth;
  }

  get isUseExistingFiguresVisible(): boolean {
    const { forMonth, forYear } = decomposeMonthCode(this.form.getMonthCodeValue());
    const currentDateHistory = find(this.reportFilter.months, { forMonth, forYear });
    return get(currentDateHistory, 'isFromHistory', false);
  }

  get isUseExistingFiguresEnabled(): boolean {
    const lastHistoryMonth = head(filter(this.reportFilter.months, { isFromHistory: true }));
    const { forMonth, forYear } = decomposeMonthCode(this.form.getMonthCodeValue());
    return !!lastHistoryMonth
      && lastHistoryMonth.forYear === forYear
      && lastHistoryMonth.forMonth === forMonth;
  }

  get isShowLeadsContactsVisible(): boolean {
    const calculation = this.form.getCalculationValue();
    return calculation === SalesManagementCalculationType.CalculateNewFigures
      || calculation === SalesManagementCalculationType.RecalculateFigures;
  }

  ngOnInit(): void {
    const { salesManagementCalculationTypes, reportFilter } = getDialogInitData(this.dialogData);
    this.reportFilter = reportFilter;
    this.salesManagementCalculationTypes = salesManagementCalculationTypes;
    this.monthSelectOptionList = values(map(this.reportFilter.months, this.composeMonthSelectOption.bind(this)));
    const monthCode = composeMonthCode(this.isSelectedMonthAvailable() ? this.input : this.reportFilter);
    this.form.build({
      ...this.getMonthReportFilter(monthCode),
      calculation: this.calculationValue(monthCode),
    });
  }

  isSelectedMonthAvailable(): boolean {
    return this.isMonthSelected()
      ? !isNil(find(this.monthSelectOptionList, ({ monthCode }) => monthCode === composeMonthCode(this.input)))
      : false;
  }

  isMonthSelected(): boolean {
    return !isNil(this.input.forMonth && this.input.forYear);
  }

  onApply(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.businessService.updateReportFilter(this.composeFilterSubmitFormValue(this.form.submitValue)).pipe(
      this.waitingService.operator())
      .subscribe(this.closeDialogOnSuccess.bind(this), this.notificationService.processAll);
  }

  onMonthChange({ value: monthCode }: MatSelectChange): void {
    this.form.markAsPristine();
    this.form.build(this.getMonthReportFilter(monthCode));
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  closeDialogOnSuccess(): void {
    this.reportService.generateUrlAndShowDialog(
      REPORT_SALES_MANAGEMENT, [
        reportField('ForTheMonthOf', this.getReportFieldValue('ForTheMonthOf')),
        reportField('ShowInactiveSalespersons', this.getReportFieldValue('ShowInactiveSalespersons')),
        reportField('ShowLeadsContacts', this.getReportFieldValue('ShowLeadsContacts')),
        reportField('IsFromBuilderPortal', true),
      ], {
        title: 'lbl_link_sales_management',
        linkText: 'lbl_sm_link_text',
      }).subscribe();
    this.dialogRef.close();
  }

  private getReportFieldValue(name: string): string | number {
// @ts-expect-error Legacy typing suppression
    return {
      ForTheMonthOf: getDateMapper(DateFormat.Date).serialize(new Date(
        this.form.getForYearValue(),
        this.form.getForMonthValue() - 1)),
      ShowInactiveSalespersons: this.form.getShowInactiveSalespersonsValue(),
      ShowLeadsContacts: this.form.getShowLeadsContactsValue(),
      Calculation: this.form.getCalculationValue(),
    }[name];
  }

  private composeFilterSubmitFormValue(reportFilter: SalesManagementReportFilter): SalesManagementReportFilter {
    return {
      ...reportFilter,
      months: this.reportFilter.months,
    };
  }

  private getMonthReportFilter(monthCode: string): SalesManagementReportFilter {
    return this.getDefaultReportFilter(monthCode);
  }

  private getDefaultReportFilter(monthCode: string): any {
    const { forMonth, forYear } = decomposeMonthCode(monthCode);
    return {
      monthCode,
      forMonth,
      forYear,
      showInactiveSalespersons: false,
      calculation: this.calculationValue(monthCode),
      showLeadsContacts: false,
    };
  }

  private calculationValue(monthCode: string): SalesManagementCalculationType {
    const { forMonth, forYear } = decomposeMonthCode(monthCode);
    const month = <SalesManagementReportHistoryItem>find(this.reportFilter.months, { forMonth, forYear });
    const lastHistoryMonth = head(filter(this.reportFilter.months, { isFromHistory: true }));
    return month && !month.isFromHistory
      ? SalesManagementCalculationType.CalculateNewFigures
      : month && month.isFromHistory && lastHistoryMonth && lastHistoryMonth.forYear === forYear && lastHistoryMonth.forMonth === forMonth        ? SalesManagementCalculationType.RecalculateFigures
        : SalesManagementCalculationType.UseExistingFigures;
  }

  private composeMonthSelectOption({ monthCode, forMonth, forYear }: SalesManagementReportHistoryItem): SalesManagementReportFilterMonthSelectOption {    return {
      monthCode,
      name: `${this.translationService.get(MONTH_NAME_LIST_MLS[forMonth])} ${forYear}`,
    };
  }
}
